import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { PageHeader } from '../components/PageHeader';
import { CheckIcon } from 'lucide-react';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const stagger = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};
const services = [{
  title: 'Environmental Assessment & Compliance',
  description: 'Comprehensive environmental assessments to ensure regulatory compliance and sustainable development across all project phases.',
  image: 'https://picsum.photos/seed/gesl-assessment/700/500',
  items: ['Environmental Impact Assessment (EIA)', 'Environmental Audit', 'Social Impact Assessment (SIA)', 'Environmental Evaluation Studies', 'Phase I & II Environmental Site Assessments']
}, {
  title: 'Monitoring & Environmental Management',
  description: 'Ongoing monitoring and management solutions to maintain environmental standards and ensure operational compliance.',
  image: 'https://picsum.photos/seed/gesl-monitoring/700/500',
  items: ['Environmental Monitoring Programmes', 'Compliance Monitoring', 'Pollution Control Studies', 'Environmental Protection & Rehabilitation', 'Oil Spill Contingency Planning']
}, {
  title: 'Sustainability & Governance',
  description: 'Strategic advisory services for sustainable business practices, ESG compliance, and stakeholder management.',
  image: 'https://picsum.photos/seed/gesl-sustainability/700/500',
  items: ['Environmental & Social Due Diligence (ESDD)', 'ESG Advisory Services', 'Climate Change & GHG Studies', 'Stakeholder Engagement & Community Relations']
}, {
  title: 'Planning & Project Development',
  description: 'Expert planning and development support for complex environmental and social projects nationwide.',
  image: 'https://picsum.photos/seed/gesl-planning/700/500',
  items: ['Resettlement Action Plans (RAP)', 'Environmental and Social Management Plans (ESMP)', 'Feasibility Studies', 'Regulatory Compliance Advisory (FMEnv, NESREA, DPR, NOSDRA, MMSD, NIWA)']
}];
export function ServicesPage() {
  return <>
      <PageHeader title="Our Services" subtitle="Comprehensive environmental, social, and sustainability consulting services tailored to meet complex regulatory and operational needs." data-id="element-641" />

      <section className="py-20 lg:py-28 bg-white" data-id="element-642">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-643">
          <div className="space-y-24 lg:space-y-32" data-id="element-644">
            {services.map((service, idx) => <motion.div key={idx} initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-80px'
          }} variants={stagger} className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-645">
                {/* Text Content */}
                <div className={idx % 2 === 1 ? 'lg:order-2' : ''} data-id="element-646">
                  <motion.div variants={fadeUp} className="flex items-center gap-3 mb-5" data-id="element-647">
                    <span className="text-gesl-gold font-bold text-sm tracking-wider" data-id="element-648">
                      0{idx + 1}
                    </span>
                    <div className="h-px w-8 bg-gesl-gold/40" data-id="element-649" />
                  </motion.div>

                  <motion.h2 variants={fadeUp} className="text-2xl md:text-3xl font-bold text-gesl-dark mb-4 leading-tight" data-id="element-650">
                    {service.title}
                  </motion.h2>

                  <motion.p variants={fadeUp} className="text-gray-500 leading-relaxed mb-8" data-id="element-651">
                    {service.description}
                  </motion.p>

                  <motion.ul variants={stagger} className="space-y-3" data-id="element-652">
                    {service.items.map((item, itemIdx) => <motion.li key={itemIdx} variants={fadeUp} className="flex items-start" data-id="element-653">
                        <CheckIcon className="h-4 w-4 text-gesl-green mr-3 mt-1 flex-shrink-0" data-id="element-654" />
                        <span className="text-gray-700 text-sm leading-relaxed" data-id="element-655">
                          {item}
                        </span>
                      </motion.li>)}
                  </motion.ul>
                </div>

                {/* Image */}
                <motion.div variants={fadeUp} className={idx % 2 === 1 ? 'lg:order-1' : ''} data-id="element-656">
                  <div className="relative overflow-hidden rounded-sm" data-id="element-657">
                    <img src={service.image} alt={service.title} className="w-full aspect-[4/3] object-cover" data-id="element-658" />
                    <div className="absolute inset-0 bg-gradient-to-t from-gesl-dark/20 to-transparent" data-id="element-659" />
                  </div>
                </motion.div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gesl-dark" data-id="element-660">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-661">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-662">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-663">
              Need Environmental Consulting?
            </h2>
            <p className="text-lg text-white/60 mb-8 max-w-2xl mx-auto" data-id="element-664">
              Partner with us for scientifically sound, innovative, and
              sustainable environmental solutions tailored to your project.
            </p>
            <a href="#contact" className="inline-block px-8 py-4 bg-gesl-gold text-white font-semibold rounded-sm hover:bg-white hover:text-gesl-dark transition-colors duration-300" data-id="element-665">
              Request a Consultation
            </a>
          </motion.div>
        </div>
      </section>
    </>;
}